import {makeGet, set, toEntries} from '@yandex-delivery/utils'
import {useCallback, useMemo} from 'react'
import {FieldPath, FieldValues, useFormContext as useReactHookFormContext, UseFormSetValue} from 'react-hook-form'

const get = makeGet<FieldValues>()

export const useFormContext = <TFieldValues extends FieldValues>() => {
  const {formState, getFieldState, register, getValues, unregister, setValue, setError, ...context} =
    useReactHookFormContext<TFieldValues>()

  const {isDirty, touchedFields, isSubmitting, isValidating, isValid, submitCount, errors, dirtyFields} = formState

  const setFieldTouched = useCallback(
    (name: string) => {
      set(name, true)(touchedFields)
    },
    [touchedFields],
  )

  const getFieldError = useCallback(
    (name: FieldPath<TFieldValues>) => {
      const {error} = getFieldState(name, formState)

      return error?.message
    },
    [formState, getFieldState],
  )

  const getFieldErrors = useCallback(
    (name: FieldPath<TFieldValues>) => {
      const {error} = getFieldState(name, formState)

      return error
    },
    [formState, getFieldState],
  )

  const setFieldError = useCallback(
    (name: FieldPath<TFieldValues>, message: string) => {
      setError(name, {message})
    },
    [setError],
  )

  const setErrors = useCallback(
    (errors: Record<FieldPath<TFieldValues>, string>) =>
      toEntries(errors).forEach(([name, message]) => {
        setError(name, {message})
      }),
    [setError],
  )

  const getIsFieldTouched = useCallback(
    (name: FieldPath<TFieldValues>) => Boolean(get(name, touchedFields)),
    [touchedFields],
  )

  const setFieldValue = useCallback<UseFormSetValue<TFieldValues>>(
    (name, value, options = {}) => {
      const opts = {shouldDirty: true, shouldTouch: true, shouldValidate: true, ...options}

      setValue(name, value, opts)
    },
    [setValue],
  )

  const isTouched = Boolean(Object.keys(touchedFields).length)

  const isDirtyFieldsExist = Boolean(Object.keys(dirtyFields).length)

  const values = getValues()

  return useMemo(
    () => ({
      dirty: isDirty,
      isDirty: isDirtyFieldsExist,
      registerField: register,
      unregisterField: unregister,
      touched: touchedFields,
      getFieldValue: getValues,
      setFieldValue,
      getFieldError,
      getFieldErrors,
      setFieldError,
      setErrors,
      getIsFieldTouched,
      isSubmitting,
      isValidating,
      isValid,
      setFieldTouched,
      submitCount,
      errors,
      values,
      setError,
      getFieldState,
      dirtyFields,
      isTouched,
      ...context,
    }),
    [
      isDirtyFieldsExist,
      isDirty,
      register,
      unregister,
      touchedFields,
      getValues,
      setFieldValue,
      getFieldError,
      getFieldErrors,
      setFieldError,
      setErrors,
      getIsFieldTouched,
      isSubmitting,
      isValidating,
      isValid,
      setFieldTouched,
      submitCount,
      errors,
      values,
      setError,
      getFieldState,
      dirtyFields,
      isTouched,
      context,
    ],
  )
}
